// src/components/surveys/questions/EmailQuestion.tsx
import React, { useState } from 'react';
import { SurveyQuestion } from '../../../types/survey.types';
import { Mail } from 'lucide-react';

interface EmailQuestionProps {
  question: SurveyQuestion;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const EmailQuestion: React.FC<EmailQuestionProps> = ({
  question,
  value = '',
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const showError = showValidation && (error || (value && !isValidEmail(value)));
  const displayError = error || (value && !isValidEmail(value) ? 'Please enter a valid email address' : '');

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">Email input preview:</div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="email"
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50"
            placeholder="example@email.com"
            disabled
          />
        </div>
        <div className="text-xs text-gray-400">
          Validates email format automatically
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <Mail
            className={`h-5 w-5 ${
              isFocused ? 'text-indigo-500' : 'text-gray-400'
            }`}
          />
        </div>
        <input
          type="email"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          placeholder="Enter email address"
          className={`w-full pl-10 pr-10 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            showError
              ? 'border-red-300 focus:border-red-500'
              : value && isValidEmail(value)
              ? 'border-green-500 bg-green-50'
              : value
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
        />
        {value && isValidEmail(value) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>
      {showError && displayError && (
        <p className="text-sm text-red-600">{displayError}</p>
      )}
    </div>
  );
};

export default EmailQuestion;
